import { forwardRef, type ReactNode } from "react";

import { cn } from "@btnet/utilities-core";

import {
    HeaderShadowProvider,
    useHeaderShadow,
} from "commonComponents/btWrappers/Frame/useHeaderShadow";

interface IFrameProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
    children: ReactNode;
    className?: string;
    "data-testid"?: string;

    /**
     * Additional pixel offset to subtract from the root's viewport height.
     * Use when the page has extra chrome outside the layout (e.g., owner portal preview alert).
     * @default 0
     */
    availableHeightOffset?: number;
}

const FrameComponent = forwardRef<HTMLDivElement, IFrameProps>(
    ({ children, className, availableHeightOffset = 0, ...otherProps }, ref) => (
        <HeaderShadowProvider>
            <div
                ref={ref}
                className={cn(
                    "bds-layout-tpl_hcf bt-list-layout-bds-enabled",
                    "flex flex-col overflow-hidden bg-gray-0",
                    className,
                )}
                style={{
                    height: `calc(100vh - var(--main-navigation-height) - var(--main-nav-owner-preview-alert-height, 0px) - var(--main-nav-owner-header-info-height, 0px) - ${availableHeightOffset}px)`,
                    containerType: "inline-size",
                    containerName: "hcf",
                }}
                {...otherProps}
            >
                {children}
            </div>
        </HeaderShadowProvider>
    ),
);
FrameComponent.displayName = "Frame";

interface IFrameContentProps extends React.HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
    className?: string;
}

const Content = forwardRef<HTMLDivElement, IFrameContentProps>(
    ({ children, className, ...otherProps }, ref) => {
        // `scrollContainerRef` is a merged callback ref — drives the
        // scroll-shadow listener (for FixedHeader) AND forwards the node to the consumer's `ref`.
        const { scrollContainerRef } = useHeaderShadow(ref);
        return (
            <div
                ref={scrollContainerRef}
                className={cn("flex flex-col flex-1 overflow-auto", className)}
                {...otherProps}
            >
                {children}
            </div>
        );
    },
);
Content.displayName = "Frame.Content";

interface IFrameHeaderProps extends React.HTMLAttributes<HTMLElement> {
    children: ReactNode;
    className?: string;
}

const FixedHeader = forwardRef<HTMLElement, IFrameHeaderProps>(
    ({ children, className, ...otherProps }, ref) => {
        const { isContentScrolled } = useHeaderShadow();
        return (
            <header
                ref={ref}
                role="banner"
                className={cn(
                    "flex flex-col shrink-0 bg-white transition-shadow duration-200 z-1",
                    isContentScrolled && "shadow-raised",
                    className,
                )}
                {...otherProps}
            >
                {children}
            </header>
        );
    },
);
FixedHeader.displayName = "Frame.FixedHeader";

const Header = forwardRef<HTMLElement, IFrameHeaderProps>(
    ({ children, className, ...otherProps }, ref) => (
        <header
            ref={ref}
            role="banner"
            className={cn("flex flex-col shrink-0 bg-white", className)}
            {...otherProps}
        >
            {children}
        </header>
    ),
);
Header.displayName = "Frame.Header";

/**
 * Tailwind page shell. Full-viewport-minus-nav flex column with `overflow:
 * hidden`, so consumers can carve out internal scroll regions via
 * `<Frame.Content>`. Pair with `<Frame.FixedHeader>` for an automatic
 * scroll-driven drop shadow, or `<Frame.Header>` for a plain header.
 */
export const Frame = Object.assign(FrameComponent, {
    /**
     * Header with automatic drop-shadow once `Frame.Content` has been
     * scrolled. Renders as a flex sibling above `Frame.Content` at full Frame
     * width — unaffected by Content's padding or scrollbar gutter. Shadow is
     * clipped to Frame's `overflow: hidden`, so on split layouts it stays
     * column-bound.
     *
     * Prefer `Frame.Header` if you don't want the scroll-driven shadow.
     */
    FixedHeader,
    /**
     * Plain header — no scroll listener, no scroll-driven shadow. Renders as
     * a flex sibling above `Frame.Content` at full Frame width.
     *
     * Use when there's no scrollable content, when you want the header to
     * stay visually flat, or when you want full control over the visual
     * treatment. Prefer `Frame.FixedHeader` if you want the scroll-driven
     * shadow.
     */
    Header,
    /**
     * Scrollable content slot. Sits below the header and owns the page's
     * internal scroll. The `flex-1 overflow-auto` pair is load-bearing:
     * combined with Frame's `overflow-hidden`, it constrains scroll to
     * this region so sticky Kendo grid headers and pagination footers stay
     * anchored.
     *
     * Attaches the scroll listener used by `useHeaderShadow` to drive
     * `Frame.FixedHeader`'s drop-shadow state.
     */
    Content,
});
